import { Component, AfterViewInit, Input, OnDestroy } from '@angular/core';
import { AirWaybillShipmentDetails } from '../../../features/components/air-waybill/air-waybill.model';
import {
  mapLabelKeyToLabels,
  pieceInfoGridColumnConfig,
  preferredShipmentDetailsOrder,
  termsAndConditionsForGenAirWaybillModal,
} from '../../../features/components/air-waybill/air-waybill.config';
import { AirWayBillService } from 'src/app/features/services/airWaybill.service';
import {
  weightMetricList,
  sizeMetricList,
} from 'src/app/shared/utility/constants';
import { CommonService } from 'src/app/shared/services/common.service';
import { CommonModule, KeyValue } from '@angular/common';
import { MaterialModule } from '../../material.module';
import { SummaryTableComponent } from '../summary-table/summary-table.component';
import { InvoiceAddressDetails } from '../../models/dynamic.model';
import { buildMultiPieceShipmentExt } from '../../utility/shipment-ext.util';

interface AirWaybillPreviewData {
  hawbExt: string;
  notes: string;
  shipperConsigneeDetails: InvoiceAddressDetails[];
  shipmentDetails: AirWaybillShipmentDetails;
  pieceTableData: any[];
  tableColumnConfig: any[];
  termsAndCondition: Record<string, string>;
}

@Component({
  selector: 'app-air-waybill-generation-preview',
  templateUrl: './air-waybill-generation-preview.component.html',
  styleUrl: './air-waybill-generation-preview.component.scss',
  standalone: true,
  imports: [CommonModule, MaterialModule, SummaryTableComponent],
})
export class AirWaybillGenerationPreviewComponent implements AfterViewInit, OnDestroy {
  private airwayBillGeneratedData: any;
  private barcodeTimer?: ReturnType<typeof setTimeout>;

  @Input()
  set airwayBillData(value: any) {
    if (value) {
      this.airwayBillGeneratedData = value;
      this.initializeAirwayBill(value);
    }
  }

  get airwayBillData(): any {
    return this.airwayBillGeneratedData;
  }

  public shipperConsigneeDetails!: InvoiceAddressDetails[];
  public shipmentDetails!: AirWaybillShipmentDetails;
  public tableColumnConfig = pieceInfoGridColumnConfig;
  public pieceTableData = [];
  public termsAndCondition!: Record<string, string>;
  public weightMetricList = weightMetricList;
  public sizeMetricList = sizeMetricList;

  public previewPages: AirWaybillPreviewData[] = [];

  constructor(
    private readonly airWayBillService: AirWayBillService,
    private readonly commonService: CommonService
  ) {}

  ngAfterViewInit() {
    this.generateAllBarcodes();
  }

  ngOnDestroy(): void {
    if (this.barcodeTimer) {
      clearTimeout(this.barcodeTimer);
    }
  }

  public renderKey(key: string): string {
    return mapLabelKeyToLabels[key];
  }

  public shipmentDetailsOrderCompareFn = (
    a: KeyValue<string, any>,
    b: KeyValue<string, any>
  ): number => {
    return (
      preferredShipmentDetailsOrder.indexOf(a.key) -
      preferredShipmentDetailsOrder.indexOf(b.key)
    );
  };

  private initializeAirwayBill(data: any) {
    this.shipperConsigneeDetails = [data.shipper, data.consignee];
    this.shipmentDetails = this.buildShipmentDetails(data);
    this.pieceTableData = data.pieceDetails.items.map((item: any) =>
      this.airWayBillService.pieceGridDataModification(item)
    );
    this.tableColumnConfig = this.modifyTableHeaders();
    this.termsAndCondition = termsAndConditionsForGenAirWaybillModal;

    // Generate preview pages for each piece
    this.previewPages = this.generatePreviewPages(data);
    this.generateAllBarcodes();
  }

  private buildShipmentDetails(data: any): AirWaybillShipmentDetails {
    return {
      createdDateAndTime: data.createdDateAndTime,
      contents: data.shipmentInformation.type,
      value: data?.shipmentInformation?.commodityList?.length
        ? data?.shipmentInformation?.commodityList[0]?.value
        : data?.shipmentInformation?.value,
      service: data.shipmentInformation.serviceType,
      pieces: data.pieceDetails?.items?.length,
      weight: data.pieceDetails?.items.reduce((sum: any, item: any) => {
        return sum + Number(item.weight) * Number(item.multiplier);
      }, 0).toFixed(2),
      description: data.shipmentInformation?.commodityList?.length
        ? data.shipmentInformation?.commodityList[0].description
        : data.shipmentInformation.description,
      hts: data.shipmentInformation?.commodityList?.length
        ? data.shipmentInformation?.commodityList[0].htsCode
        : data.shipmentInformation?.htsCode,
      reference: data.shipmentInformation.reference,
    };
  }
  private generatePreviewPages(data: any): AirWaybillPreviewData[] {
    const pieces = data.pieceDetails?.items || [];

    const totalAirwaybillCount = pieces.reduce((sum: number, piece: any) => {
      const multiplier = Number(piece.multiplier) || 1;
      return sum + multiplier;
    }, 0);

    if (totalAirwaybillCount <= 1) {
      return [
        {
          hawbExt: data.hawbExt || '',
          notes: data.notes || '',
          shipperConsigneeDetails: [data.shipper, data.consignee],
          shipmentDetails: this.buildShipmentDetails(data),
          pieceTableData: pieces.map((item: any) =>
            this.airWayBillService.pieceGridDataModification(item)
          ),
          tableColumnConfig: this.modifyTableHeaders(),
          termsAndCondition: termsAndConditionsForGenAirWaybillModal,
        },
      ];
    }

    const previewPages: AirWaybillPreviewData[] = [];
    const baseHawbExt = data.hawbExt || '';
    let pageCounter = 0;
    pieces.forEach((piece: any) => {
      const multiplier = Number(piece.multiplier) || 1;
      for (let i = 0; i < multiplier; i++) {
        const uniqueHawbExt = buildMultiPieceShipmentExt(baseHawbExt, pageCounter);
        previewPages.push({
          hawbExt: uniqueHawbExt,
          notes: data.notes || '',
          shipperConsigneeDetails: [data.shipper, data.consignee],
          shipmentDetails: this.buildShipmentDetails(data),
          pieceTableData: pieces.map((item: any) =>
            this.airWayBillService.pieceGridDataModification(item)
          ),
          tableColumnConfig: this.modifyTableHeaders(),
          termsAndCondition: termsAndConditionsForGenAirWaybillModal,
        });
        pageCounter++;
      }
    });

    return previewPages;
  }

  private generateAllBarcodes(): void {
    if (this.barcodeTimer) {
      clearTimeout(this.barcodeTimer);
    }
    this.barcodeTimer = setTimeout(() => {
      this.barcodeTimer = undefined;
      for (let index = 0; index < this.previewPages.length; index++) {
        const page = this.previewPages[index];
        const barcodeSelector = `#barcode-${index}`;
        if (page.hawbExt) {
          this.commonService.generateBarcode(barcodeSelector, page.hawbExt);
        }
      }
    }, 0);
  }

  private modifyTableHeaders() {
    return this.tableColumnConfig.map(col => {
      if (col.key === 'weight') {
        return {
          ...col,
          label: `Weight (${this.commonService.setMeasurementOptionsValues().weightUnitValue})`,
        };
      }
      if (col.key === 'volumetric') {
        return {
          ...col,
          label: `Volumetric (${this.commonService.setMeasurementOptionsValues().weightUnitValue})`,
        };
      }
      if (col.key === 'dimensions') {
        return {
          ...col,
          label: `Dimensions (${this.commonService.setMeasurementOptionsValues().sizeUnitValue})`,
        };
      }
      return col;
    });
  }
}
